/**
 * 三层漏斗组件 — 对齐视觉基线 .funnel（design/assets/theme.css §漏斗）。
 *
 * 结构：三层横条（宽度按计数比例，第一层恒 100%）+ 层间转化率
 * + 断点层 warning 色（index_rate < baseline_index_rate 时收录层标警）+ 钻取链接位。
 * 色值全部走 theme token（brand.50/brand.500、semanticBg.warning、warning.main）。
 */
import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import type { LedgerSummary } from "../types";

export interface FunnelViewProps {
  /** 漏斗聚合（契约见 kickoff 02 §4 /api/index-ledger/summary）。 */
  summary: LedgerSummary;
  /** 钻取链接位（如 /index-ledger 收录台账）。 */
  drilldownLink?: string;
}

/** 0–1 比率 → 百分比文本。 */
function pct(rate: number, digits = 1): string {
  return `${(rate * 100).toFixed(digits)}%`;
}

export default function FunnelView({ summary, drilldownLink }: FunnelViewProps) {
  const { produced, indexed, ai_retrievable, index_rate, retrieval_rate, baseline_index_rate } =
    summary;
  // 断点判定：收录率低于基线 → 收录层标 warning（对齐 .f-bar.warn + .f-rate 断点说明）
  const broken = index_rate < baseline_index_rate;

  /** 层宽：按计数占首层比例；保底 120px 防止文字裁切。 */
  const widthOf = (count: number) =>
    produced > 0 ? `${Math.max((count / produced) * 100, 0)}%` : "0%";

  const stages: {
    label: string;
    count: number;
    suffix?: string;
    warn?: boolean;
    note?: string;
  }[] = [
    { label: "内容产出", count: produced, suffix: " 篇" },
    {
      label: "搜索引擎收录",
      count: indexed,
      warn: broken,
      note: broken ? `断点 ⚠ 低于基线 ${pct(baseline_index_rate, 0)}` : undefined,
    },
    { label: "AI 可检索", count: ai_retrievable },
  ];
  const arrows = [`↓ 收录率 ${pct(index_rate)}`, `↓ 可检索率 ${pct(retrieval_rate)}`];

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {stages.map((s, i) => (
          <Box key={s.label}>
            {i > 0 && (
              <Typography
                sx={{ textAlign: "center", color: "text.disabled", fontSize: 12, py: 0.25 }}
              >
                {arrows[i - 1]}
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: i === 0 ? "100%" : widthOf(s.count),
                  minWidth: 120,
                  height: 44,
                  borderRadius: 2,
                  // 对齐 .f-bar：brand.50 底 + brand.500 边；断点层 .f-bar.warn
                  bgcolor: s.warn ? "semanticBg.warning" : "brand.50",
                  border: "1px solid",
                  borderColor: s.warn ? "warning.main" : "brand.500",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.75,
                  boxSizing: "border-box",
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: "text.primary" }}>
                  {s.label}
                </Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: "text.primary" }}>
                  {s.count}
                  {s.suffix || ""}
                </Typography>
              </Box>
              {s.note && (
                <Typography
                  sx={{ fontSize: 12, color: "warning.main", width: 120, flexShrink: 0 }}
                >
                  {s.note}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* 钻取链接位 */}
      {drilldownLink && (
        <Box sx={{ mt: 1.5, textAlign: "right" }}>
          <Link
            component={RouterLink}
            to={drilldownLink}
            sx={{ fontSize: 12, color: "primary.main", textDecoration: "none" }}
          >
            收录台账 ›
          </Link>
        </Box>
      )}
    </Box>
  );
}
